import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DevMode from './DevMode';
import DevModeTodoList from './DevModeTodoList';
import AIChatMinimal from './AIChatMinimal';
import ClaudeProgress from './ClaudeProgress';
import { useAPIMonitor } from '../hooks/useAPIMonitor';
import { briansFunMarkets } from '../utils/mockMarkets';

interface Market {
  id: string;
  question: string;
  description: string;
  options: MarketOption[];
  endDate: string;
  creatorId?: string;
  slug: string;
  totalPool: number;
  status: 'active' | 'pending' | 'resolved';
  resolution?: string;
  createdAt: string;
}

interface MarketOption {
  id: string;
  text: string;
  probability: number;
  totalBets: number;
  color: string;
  wagered?: number; // Track amount wagered on this option
}

interface Bet {
  marketId: string;
  optionId: string;
  amount: number;
  userId: string;
  timestamp: string;
  marketQuestion?: string;
  optionText?: string;
  shares?: number;
  status?: 'active' | 'won' | 'lost';
  payout?: number;
}

interface User {
  id: string;
  name: string;
  balance: number;
  avatar: string;
  wins: number;
  losses: number;
  totalProfit: number;
  rank?: number;
}

export default function CreateMarket() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { apiCalls, monitoredFetch } = useAPIMonitor();

  const [mode, setMode] = useState<'home' | 'create' | 'browse' | 'market' | 'portfolio' | 'leaderboard' | 'progress' | null>('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [marketQuestion, setMarketQuestion] = useState('');
  const [marketDescription, setMarketDescription] = useState('');
  const [marketOptions, setMarketOptions] = useState<string[]>(['', '']);
  const [endDate, setEndDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdMarket, setCreatedMarket] = useState<Market | null>(null);
  const [activeMarkets, setActiveMarkets] = useState<Market[]>([]);
  const [currentMarket, setCurrentMarket] = useState<Market | null>(null);
  const [betAmount, setBetAmount] = useState(10);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [isPlacingBet, setIsPlacingBet] = useState(false);
  const [userBets, setUserBets] = useState<Bet[]>([]);
  const [isLoadingMarket, setIsLoadingMarket] = useState(false);
  const [leaderboard, setLeaderboard] = useState<User[]>([]);

  // User profile with auth integration
  const [currentUser, setCurrentUser] = useState<User>(() => {
    // First check for authenticated session
    const session = localStorage.getItem('session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);

        // Check if session is still valid (24 hour expiry)
        if (sessionData.expiresAt && new Date(sessionData.expiresAt) < new Date()) {
          console.log('Session expired, clearing...');
          localStorage.removeItem('session');
        } else {
          // Valid session - create user from session data
          return {
            id: sessionData.userId,
            name: sessionData.email?.split('@')[0] || 'User', // Use email prefix as name
            email: sessionData.email,
            balance: sessionData.balance || 1000,
            avatar: '🔐', // Authenticated user icon
            wins: 0,
            losses: 0,
            totalProfit: 0
          };
        }
      } catch (error) {
        console.error('Invalid session data:', error);
        localStorage.removeItem('session');
      }
    }

    // Fall back to stored user
    const stored = localStorage.getItem('futrbet_user');
    if (stored) return JSON.parse(stored);

    // Create demo user if no auth
    const newUser: User = {
      id: `demo-${Date.now()}`,
      name: `Guest${Math.floor(Math.random() * 9999)}`,
      balance: 1000,
      avatar: ['🧑‍💻', '👨‍💼', '👩‍💼', '🧑‍🚀', '👨‍🔬', '👩‍🔬'][Math.floor(Math.random() * 6)],
      wins: 0,
      losses: 0,
      totalProfit: 0
    };
    localStorage.setItem('futrbet_user', JSON.stringify(newUser));
    return newUser;
  });

  // Dev mode and engineer mode check
  const urlParams = new URLSearchParams(window.location.search);
  const devMode = urlParams.get('devMode') === '1';
  const [engineerMode, setEngineerMode] = useState(urlParams.get('engineer') === '1');
  const [engineerEvents, setEngineerEvents] = useState<Array<{time: string, type: string, message: string, data?: any}>>([]);
  const [showBetSuccess, setShowBetSuccess] = useState(false);

  // Engineer mode event logger
  const logEvent = (type: string, message: string, data?: any) => {
    if (engineerMode) {
      const event = {
        time: new Date().toISOString().split('T')[1].slice(0, -1),
        type,
        message,
        data
      };
      setEngineerEvents(prev => [...prev, event].slice(-50)); // Keep last 50 events
    }
  };

  // Load active markets on mount
  useEffect(() => {
    loadActiveMarkets();
    loadUserData();

    // If there's a slug in the URL, load that specific market
    if (slug) {
      loadMarketBySlug(slug);
    }
  }, [slug]);

  const loadActiveMarkets = async () => {
    try {
      const response = await monitoredFetch('https://futr-bet-api.b-9f2.workers.dev/api/markets');
      const data = await response.json();

      if (data.success) {
        // Combine API markets with Brian's fun markets
        const apiMarkets = data.data || [];
        const combined = [...briansFunMarkets, ...apiMarkets];
        // Remove duplicates based on ID
        const unique = combined.filter((market, index, self) =>
          index === self.findIndex((m) => m.id === market.id)
        );
        setActiveMarkets(unique);
      } else {
        console.error('Failed to load markets:', data.error);
        // Even if API fails, show the fun markets
        setActiveMarkets(briansFunMarkets);
      }
    } catch (error) {
      console.error('Failed to load markets:', error);
      // Even if API fails, show the fun markets
      setActiveMarkets(briansFunMarkets);
    }
  };

  const loadMarketBySlug = async (marketSlug: string) => {
    setIsLoadingMarket(true);
    logEvent('info', `Loading market by slug: ${marketSlug}`);

    try {
      // First check if it's a mock market
      const mockMarket = briansFunMarkets.find(m => m.slug === marketSlug || m.id === marketSlug);
      if (mockMarket) {
        logEvent('success', 'Found mock market', mockMarket);
        setCurrentMarket(mockMarket as any);
        setMode('market');
        setIsLoadingMarket(false);
        return;
      }

      // Try API
      logEvent('api', `Fetching from API: /api/markets/${marketSlug}`);
      const response = await monitoredFetch(`https://futr-bet-api.b-9f2.workers.dev/api/markets/${marketSlug}`);
      const data = await response.json();
      logEvent('api', `Response status: ${response.status}`, data);

      if (data.success) {
        logEvent('success', 'Market loaded from API', data.data);
        setCurrentMarket(data.data);
        setMode('market');
      } else {
        logEvent('error', `Market not found: ${data.error}`, data);
        console.error('Market not found:', data.error);
        alert('Market not found: ' + marketSlug);
        navigate('/');
      }
    } catch (error) {
      logEvent('error', `Failed to load market: ${error}`, error);
      console.error('Failed to load market:', error);
      alert('Failed to load market');
      navigate('/');
    } finally {
      setIsLoadingMarket(false);
    }
  };

  const loadUserData = () => {
    // Load user bets from localStorage
    const savedBets = localStorage.getItem('userBets');
    if (savedBets) setUserBets(JSON.parse(savedBets));

    // Load leaderboard
    loadLeaderboard();
  };

  const loadLeaderboard = () => {
    // Generate mock leaderboard for now
    const mockLeaders: User[] = [
      { id: '1', name: 'CryptoKing', balance: 5420, avatar: '👑', wins: 45, losses: 12, totalProfit: 4420, rank: 1 },
      { id: '2', name: 'MarketMaven', balance: 3200, avatar: '🎯', wins: 38, losses: 15, totalProfit: 2200, rank: 2 },
      { id: '3', name: 'BetMaster', balance: 2800, avatar: '🏆', wins: 32, losses: 18, totalProfit: 1800, rank: 3 },
      { id: '4', name: 'Prophet', balance: 2400, avatar: '🔮', wins: 28, losses: 14, totalProfit: 1400, rank: 4 },
      { id: '5', name: 'Nostradamus', balance: 2100, avatar: '📈', wins: 25, losses: 10, totalProfit: 1100, rank: 5 },
      currentUser
    ].sort((a, b) => b.totalProfit - a.totalProfit);

    mockLeaders.forEach((user, index) => {
      user.rank = index + 1;
    });

    setLeaderboard(mockLeaders);
  };

  const updateUserBalance = (amount: number, won: boolean = false) => {
    const updatedUser = {
      ...currentUser,
      balance: currentUser.balance + amount,
      wins: won ? currentUser.wins + 1 : currentUser.wins,
      losses: !won && amount < 0 ? currentUser.losses + 1 : currentUser.losses,
      totalProfit: currentUser.totalProfit + amount
    };
    setCurrentUser(updatedUser);
    localStorage.setItem('futrbet_user', JSON.stringify(updatedUser));
  };

  const handleCreateMarket = async () => {
    if (!marketQuestion || marketOptions.filter(o => o).length < 2 || !endDate) {
      alert('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    try {
      // Call the real API
      const response = await monitoredFetch('https://futr-bet-api.b-9f2.workers.dev/api/markets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: marketQuestion,
          description: marketDescription,
          options: marketOptions.filter(o => o),
          endDate,
          category: 'general',
          creatorId: 'user-' + Math.random().toString(36).substr(2, 9)
        })
      });

      const result = await response.json();

      if (result.success) {
        // Fetch the created market details
        const marketResponse = await monitoredFetch(`https://futr-bet-api.b-9f2.workers.dev/api/markets/${result.data.slug}`);
        const marketData = await marketResponse.json();

        if (marketData.success) {
          setCreatedMarket(marketData.data);
          // Reload active markets
          await loadActiveMarkets();
          // Navigate to the new market page after a short delay
          setTimeout(() => {
            navigate(`/market/${marketData.data.slug || marketData.data.id}`);
          }, 2000);
        }
      } else {
        alert('Failed to create market: ' + (result.error || 'Unknown error'));
      }

      // Reset form
      setMarketQuestion('');
      setMarketDescription('');
      setMarketOptions(['', '']);
      setEndDate('');
    } catch (error) {
      console.error('Error creating market:', error);
      alert('Failed to create market: ' + error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handlePlaceBet = async (market: Market, optionId: string) => {
    // Check if user is authenticated
    const session = localStorage.getItem('session');
    if (!session || currentUser.id.startsWith('demo-')) {
      // Save intent to localStorage so we can return here after login
      localStorage.setItem('loginIntent', JSON.stringify({
        action: 'place-bet',
        marketId: market.id,
        marketSlug: market.slug,
        optionId,
        betAmount,
        returnUrl: window.location.pathname
      }));

      // Just navigate to login - no annoying alert!
      navigate('/otp-login');
      return;
    }

    if (!selectedOption || betAmount <= 0) {
      alert('Please select an option and enter a valid amount');
      return;
    }

    if (betAmount > currentUser.balance) {
      alert('Insufficient balance');
      return;
    }

    setIsPlacingBet(true);
    logEvent('info', 'Starting bet placement', {
      marketId: market.id,
      optionId,
      amount: betAmount,
      userId: currentUser.id
    });

    try {
      // Check if this is a mock market
      const isMockMarket = briansFunMarkets.some(m => m.id === market.id);

      let response;
      let responseData;

      if (isMockMarket) {
        // Simulate bet for mock markets
        logEvent('info', 'Mock market detected - simulating bet', { marketId: market.id });

        // Simulate success response
        response = { ok: true, status: 200 };
        responseData = {
          success: true,
          message: 'Mock bet placed successfully'
        };

        // Update balance locally for mock bets
        logEvent('info', 'Updating balance for mock bet');
      } else {
        // Prepare bet data for real markets
        const betData = {
          market_id: market.id,
          user_id: currentUser.id,
          option_id: optionId,
          amount: betAmount,
          shares: betAmount // Simplified 1:1 for now
        };

        logEvent('api', 'Sending bet to API', betData);

        // Call API to place bet
        response = await monitoredFetch('https://futr-bet-api.b-9f2.workers.dev/api/bets', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(betData)
        });

        responseData = await response.json();
        logEvent('api', `Response: ${response.status}`, responseData);
      }

      if (response.ok) {
        const option = market.options.find(o => o.id === optionId);
        const newBet: Bet = {
          marketId: market.id,
          optionId,
          amount: betAmount,
          userId: currentUser.id,
          timestamp: new Date().toISOString(),
          marketQuestion: market.question,
          optionText: option?.text || '',
          shares: betAmount,
          status: 'active'
        };

        // Update user balance - use balance from API response if available
        const newBalance = responseData?.user?.balance;
        if (newBalance !== undefined) {
          // Use exact balance from server
          const updatedUser = { ...currentUser, balance: newBalance };
          setCurrentUser(updatedUser);

          // Update session with new balance
          const session = localStorage.getItem('session');
          if (session) {
            const sessionData = JSON.parse(session);
            sessionData.balance = newBalance;
            localStorage.setItem('session', JSON.stringify(sessionData));
          } else {
            localStorage.setItem('futrbet_user', JSON.stringify(updatedUser));
          }
        } else {
          // Fallback to client-side calculation
          updateUserBalance(-betAmount);
        }

        // Save bet locally
        const updatedBets = [...userBets, newBet];
        setUserBets(updatedBets);
        localStorage.setItem('userBets', JSON.stringify(updatedBets));

        // Show success state instead of alert
        logEvent('success', 'Bet placed successfully!', newBet);

        // Store bet details for success display
        localStorage.setItem('lastBet', JSON.stringify({
          market: market.question,
          option: option?.text,
          amount: betAmount,
          newBalance: currentUser.balance - betAmount,
          timestamp: new Date().toISOString()
        }));

        // Show success modal
        setShowBetSuccess(true);

        // Update market options with new wagered amounts locally
        const updatedMarkets = activeMarkets.map(m => {
          if (m.id === market.id) {
            return {
              ...m,
              totalPool: m.totalPool + betAmount,
              options: m.options.map(opt =>
                opt.id === option.id
                  ? { ...opt, totalBets: (opt.totalBets || 0) + betAmount }
                  : opt
              )
            };
          }
          return m;
        });
        setActiveMarkets(updatedMarkets);

        // Don't auto-close modal - let user close it
        // Reset bet amount for next bet
        setTimeout(() => {
          setBetAmount(10);
          setSelectedOption('');
        }, 500);

        // Refresh market data
        await loadActiveMarkets();
      } else {
        // Handle error response
        logEvent('error', `Bet failed: ${responseData.error || 'Unknown error'}`, responseData);
        alert(`Failed to place bet: ${responseData.error || 'Please try again'}`);
      }
    } catch (error) {
      logEvent('error', `Exception during bet: ${error}`, error);
      alert(`Failed to place bet: ${error instanceof Error ? error.message : 'Network error'}`);
    } finally {
      setIsPlacingBet(false);
    }
  };

  // Filter markets based on search
  const getFilteredMarkets = () => {
    let filtered = activeMarkets;

    if (searchQuery) {
      filtered = filtered.filter(m =>
        m.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      // Add category filtering when categories are implemented
    }

    return filtered;
  };

  const addOption = () => {
    if (marketOptions.length < 6) {
      setMarketOptions([...marketOptions, '']);
    }
  };

  const updateOption = (index: number, value: string) => {
    const updated = [...marketOptions];
    updated[index] = value;
    setMarketOptions(updated);
  };

  const removeOption = (index: number) => {
    if (marketOptions.length > 2) {
      const updated = marketOptions.filter((_, i) => i !== index);
      setMarketOptions(updated);
    }
  };

  // Get component state for DevMode
  const getActiveComponents = () => {
    const components = [];

    if (mode === 'home') {
      components.push({
        name: 'HomePage',
        props: { marketsCount: activeMarkets.length },
        state: { mode, userBalance: currentUser.balance, userName: currentUser.name },
        hooks: ['useState', 'useEffect']
      });
    } else if (mode === 'create') {
      components.push({
        name: 'CreateMarket',
        props: {},
        state: { marketQuestion, marketOptions, endDate, isCreating },
        hooks: ['useState']
      });
    } else if (mode === 'browse') {
      components.push({
        name: 'BrowseMarkets',
        props: { markets: activeMarkets },
        state: { totalMarkets: activeMarkets.length },
        hooks: ['useState', 'useEffect']
      });
    } else if (mode === 'market' && currentMarket) {
      components.push({
        name: 'MarketDetail',
        props: { market: currentMarket },
        state: { selectedOption, betAmount, isPlacingBet },
        hooks: ['useState', 'useParams']
      });
    } else if (mode === 'portfolio') {
      components.push({
        name: 'Portfolio',
        props: {},
        state: { userBalance: currentUser.balance, bets: userBets.length, totalProfit: currentUser.totalProfit },
        hooks: ['useState']
      });
    }

    return components;
  };

  // HOME VIEW - Main landing
  if (mode === 'home') {
    return (
      <>
        {devMode && <DevModeTodoList />}
        <AIChatMinimal />
        <DevMode
          currentMode={mode}
          activeComponents={getActiveComponents()}
          apiCalls={apiCalls}
          userState={{
            balance: currentUser.balance,
            activeBets: userBets.length,
            totalWagered: userBets.reduce((sum, bet) => sum + bet.amount, 0),
            user: currentUser
          }}
          marketState={{
            totalMarkets: activeMarkets.length,
            activeMarkets: activeMarkets.filter(m => m.status === 'active').length,
            totalVolume: activeMarkets.reduce((sum, m) => sum + (m.totalPool || 0), 0),
            currentMarket
          }}
        />
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-orange-50 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-400 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Header */}
          <header className="px-4 py-6">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">F</span>
                </div>
                <span className="font-bold text-xl text-gray-900">FutrBet</span>
              </div>
              <nav className="hidden md:flex items-center gap-6 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-green-600 font-semibold">
                    {currentUser.avatar} {currentUser.name}
                  </span>
                  {currentUser.id.startsWith('demo-') && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-bold">DEMO</span>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-300 rounded-lg px-3 py-1 shadow-sm">
                      <div className="text-[10px] text-green-600 uppercase tracking-wider">Balance</div>
                      <div className="text-lg font-bold text-green-800 -mt-0.5">
                        ${currentUser.balance.toLocaleString()}
                      </div>
                    </div>
                    {currentUser.balance < 100 && (
                      <button
                        onClick={() => {
                          const newBalance = currentUser.balance + 1000;
                          const updatedUser = { ...currentUser, balance: newBalance };
                          setCurrentUser(updatedUser);

                          // Update session if authenticated
                          const session = localStorage.getItem('session');
                          if (session) {
                            const sessionData = JSON.parse(session);
                            sessionData.balance = newBalance;
                            localStorage.setItem('session', JSON.stringify(sessionData));
                          } else {
                            localStorage.setItem('futrbet_user', JSON.stringify(updatedUser));
                          }

                          alert('🎉 Added $1,000 play money to your account!');
                        }}
                        className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 font-bold animate-pulse"
                      >
                        + Add Funds
                      </button>
                    )}
                  </div>
                </div>
                <button onClick={() => setMode('leaderboard')} className="text-gray-600 hover:text-gray-900">Leaderboard</button>
                <button onClick={() => setMode('progress')} className="text-purple-600 hover:text-purple-700 font-bold">🤖 Progress</button>
                <a href="/showcase" className="text-purple-600 hover:text-purple-700 font-bold">✨ Showcase</a>
                <a href="/architecture" className="text-gray-600 hover:text-gray-900">Architecture</a>
                <a href="/help" className="text-gray-600 hover:text-gray-900">How it Works</a>

                {/* Auth Buttons */}
                {currentUser.id.startsWith('demo-') ? (
                  <a
                    href="/otp-login"
                    className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-semibold"
                  >
                    Login
                  </a>
                ) : (
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to logout?')) {
                        localStorage.removeItem('session');
                        window.location.reload();
                      }
                    }}
                    className="px-4 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 font-semibold"
                  >
                    Logout
                  </button>
                )}

                <button
                  onClick={() => setEngineerMode(!engineerMode)}
                  className={`px-3 py-1 rounded ${engineerMode ? 'bg-yellow-400 text-black' : 'bg-gray-200 text-gray-600'}`}
                >
                  🔧 Engineer
                </button>
              </nav>
            </div>
          </header>

          {/* Hero Section */}
          <div className="max-w-6xl mx-auto px-4 py-20 text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-600 to-orange-600 bg-clip-text text-transparent">
                Bet on the Future
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
              Create prediction markets, place bets on future events, and compete with friends to see who predicts best.
            </p>

            {/* Action Cards */}
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <button
                onClick={() => setMode('create')}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="text-5xl mb-4">🎯</div>
                  <h3 className="text-xl font-bold mb-2">Create Market</h3>
                  <p className="text-gray-600 text-sm">Ask a question about the future and let others bet on outcomes</p>
                </div>
              </button>

              <button
                onClick={() => setMode('browse')}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="text-5xl mb-4">📊</div>
                  <h3 className="text-xl font-bold mb-2">Browse Markets</h3>
                  <p className="text-gray-600 text-sm">Explore active markets and place your bets</p>
                </div>
              </button>

              <button
                onClick={() => setMode('portfolio')}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="text-5xl mb-4">💼</div>
                  <h3 className="text-xl font-bold mb-2">My Portfolio</h3>
                  <p className="text-gray-600 text-sm">Track your bets and see your performance</p>
                </div>
              </button>
            </div>

            {/* Featured Markets */}
            <div className="mt-20">
              <h2 className="text-2xl font-bold mb-8">🔥 Trending Markets</h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {activeMarkets && activeMarkets.length > 0 ? activeMarkets.slice(0, 2).map(market => (
                  <div key={market.id} className="bg-white rounded-xl p-6 shadow-md text-left">
                    <h3 className="font-bold text-lg mb-2">{market.question}</h3>
                    <div className="flex gap-2 mb-4">
                      {market.options && market.options.length > 0 ? market.options.map(option => (
                        <div key={option.id} className="flex-1">
                          <div className="text-sm text-gray-600">{option.text}</div>
                          <div className="font-bold" style={{ color: option.color }}>{option.probability}%</div>
                        </div>
                      )) : null}
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Pool: ${market.totalPool ? market.totalPool.toLocaleString() : '0'}</span>
                      <span>Ends: {market.endDate ? new Date(market.endDate).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                )) : (
                  <div className="col-span-2 text-center text-gray-500">
                    <p>No markets available yet. Create the first one!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // CREATE MARKET VIEW
  if (mode === 'create') {
    if (createdMarket) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-xl p-8 max-w-2xl w-full border border-green-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full mb-4">
                <span className="text-5xl">🎉</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Market Created!</h2>
              <p className="text-gray-600">Your prediction market is now live</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h3 className="font-bold mb-2">{createdMarket.question}</h3>
              <p className="text-sm text-gray-600 mb-4">{createdMarket.description}</p>
              <div className="flex gap-2">
                {createdMarket.options.map(option => (
                  <div key={option.id} className="flex-1 bg-white rounded-lg p-2 text-center">
                    <div className="font-semibold" style={{ color: option.color }}>{option.text}</div>
                    <div className="text-sm text-gray-500">{option.probability}%</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`https://futr-bet.pages.dev/market/${createdMarket.slug}`);
                }}
                className="flex-1 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
              >
                Copy Link
              </button>
              <button
                onClick={() => {
                  setCreatedMarket(null);
                  setMode('home');
                }}
                className="flex-1 py-3 bg-gray-200 text-gray-800 font-semibold rounded-xl hover:bg-gray-300 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-2xl w-full border border-green-100">
          <button onClick={() => { setMode('home'); navigate('/'); }} className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
              <span className="text-3xl">🎯</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Prediction Market</h2>
            <p className="text-gray-600">Ask a question about the future</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Market Question *</label>
              <input
                type="text"
                value={marketQuestion}
                onChange={(e) => setMarketQuestion(e.target.value)}
                placeholder="e.g., Will Tesla stock reach $500 by end of 2025?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (optional)</label>
              <textarea
                value={marketDescription}
                onChange={(e) => setMarketDescription(e.target.value)}
                placeholder="Add context or resolution criteria..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none h-24"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Outcome Options *</label>
              <div className="space-y-2">
                {marketOptions.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                    />
                    {marketOptions.length > 2 && (
                      <button
                        onClick={() => removeOption(index)}
                        className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {marketOptions.length < 6 && (
                <button
                  onClick={addOption}
                  className="mt-2 text-green-600 hover:text-green-700 text-sm font-medium"
                >
                  + Add Option
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none"
              />
            </div>

            <button
              onClick={handleCreateMarket}
              disabled={isCreating}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg disabled:opacity-50"
            >
              {isCreating ? 'Creating Market...' : 'Create Market'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // BROWSE MARKETS VIEW
  if (mode === 'browse') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => { setMode('home'); navigate('/'); }} className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-4">Active Markets</h1>

            {/* Search Bar */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search markets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
              >
                <option value="all">All Categories</option>
                <option value="sports">Sports</option>
                <option value="crypto">Crypto</option>
                <option value="politics">Politics</option>
                <option value="entertainment">Entertainment</option>
                <option value="tech">Tech</option>
              </select>
            </div>

            {getFilteredMarkets().length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold mb-2">No markets found</h3>
                <p className="text-gray-600">Try adjusting your search or create a new market!</p>
              </div>
            ) : (
              <p className="text-gray-600 mb-4">{getFilteredMarkets().length} markets found</p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {getFilteredMarkets().map(market => (
              <div key={market.id} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <h2 className="text-xl font-bold mb-3">{market.question}</h2>
                <p className="text-sm text-gray-600 mb-4">{market.description}</p>

                <div className="space-y-2 mb-4">
                  {market.options && market.options.length > 0 ? market.options.map(option => (
                    <div key={option.id} className="flex items-center justify-between">
                      <span className="font-medium">{option.text}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${option.probability || 0}%`,
                              backgroundColor: option.color || '#gray'
                            }}
                          />
                        </div>
                        <span className="font-bold text-sm" style={{ color: option.color || '#gray' }}>
                          {option.probability || 0}%
                        </span>
                      </div>
                    </div>
                  )) : <div className="text-gray-500 text-sm">No options available</div>}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    <div>Pool: ${(market.totalPool || 0).toLocaleString()}</div>
                    <div>Ends: {market.endDate ? new Date(market.endDate).toLocaleDateString() : 'N/A'}</div>
                  </div>
                  <button
                    onClick={() => {
                      // Check auth FIRST before showing market
                      const session = localStorage.getItem('session');
                      if (!session || currentUser.id.startsWith('demo-')) {
                        // Save where they wanted to go
                        localStorage.setItem('loginIntent', JSON.stringify({
                          action: 'view-market-to-bet',
                          marketId: market.id,
                          marketSlug: market.slug || market.id,
                          marketQuestion: market.question,
                          returnUrl: `/market/${market.slug || market.id}`
                        }));

                        // Go to login instead
                        navigate('/otp-login');
                      } else {
                        // Authenticated - go to market
                        navigate(`/market/${market.slug || market.id}`);
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {currentUser.id.startsWith('demo-') ? '🔐 Login to Bet' : 'Place Bet'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // LEADERBOARD VIEW
  if (mode === 'leaderboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 p-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => { setMode('home'); navigate('/'); }} className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>

          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h1 className="text-3xl font-bold mb-2">🏆 Leaderboard</h1>
            <p className="text-gray-600">Top predictors by total profit</p>
          </div>

          <div className="space-y-4">
            {leaderboard.map((user, index) => (
              <div
                key={user.id}
                className={`bg-white rounded-xl shadow-lg p-6 flex items-center justify-between transition-all hover:shadow-xl ${
                  user.id === currentUser.id ? 'ring-2 ring-green-500' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`text-3xl font-bold ${
                    index === 0 ? 'text-yellow-500' :
                    index === 1 ? 'text-gray-400' :
                    index === 2 ? 'text-orange-600' :
                    'text-gray-600'
                  }`}>
                    #{user.rank}
                  </div>
                  <div className="text-3xl">{user.avatar}</div>
                  <div>
                    <div className="font-bold text-lg">
                      {user.name}
                      {user.id === currentUser.id && <span className="ml-2 text-sm text-green-600">(You)</span>}
                    </div>
                    <div className="text-sm text-gray-600">
                      {user.wins}W - {user.losses}L
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">+${user.totalProfit.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">Balance: ${user.balance.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // PROGRESS VIEW
  if (mode === 'progress') {
    return <ClaudeProgress />;
  }

  // PORTFOLIO VIEW
  if (mode === 'portfolio') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => { setMode('home'); navigate('/'); }} className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Portfolio Overview</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-gray-600 mb-1">Current Balance</div>
                <div className="text-3xl font-bold text-green-600">${currentUser.balance.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Active Bets</div>
                <div className="text-3xl font-bold">{userBets.length}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Total Wagered</div>
                <div className="text-3xl font-bold text-orange-600">
                  ${userBets.reduce((sum, bet) => sum + bet.amount, 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold mb-4">Your Bets</h3>
            {userBets.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active bets yet</p>
            ) : (
              <div className="space-y-3">
                {userBets.map((bet, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Market #{bet.marketId}</div>
                      <div className="text-sm text-gray-600">Option: {bet.optionId}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">${bet.amount}</div>
                      <div className="text-sm text-gray-500">{new Date(bet.timestamp).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // MARKET DETAIL VIEW
  if (mode === 'market' && currentMarket) {
    // Show engineer panel if in engineer mode
    if (engineerMode) {
      return (
        <div className="min-h-screen bg-black p-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <button
                onClick={() => {
                  navigate('/');
                }}
                className="text-green-400 hover:text-green-300 flex items-center gap-2"
              >
                ← Back to Markets
              </button>
              <button
                onClick={() => setEngineerMode(false)}
                className="px-4 py-2 bg-yellow-400 text-black rounded hover:bg-yellow-300"
              >
                Exit Engineer Mode
              </button>
            </div>

            {/* Engineer iframe */}
            <div className="border border-green-400 rounded">
              <iframe
                src="/bet-engineer"
                className="w-full h-[800px]"
                title="Bet Engineer View"
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-orange-50 p-4">
        {isLoadingMarket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-xl">
              <div className="text-xl">Loading market...</div>
            </div>
          </div>
        )}

        {/* Bet Success Modal */}
        {showBetSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md mx-4 transform scale-100">
              <div className="text-center">
                <div className="text-6xl mb-4 animate-bounce">🎉</div>
                <h2 className="text-3xl font-bold mb-2 text-green-600 animate-pulse">Bet Placed Successfully!</h2>
                <p className="text-gray-600 mb-4">
                  You bet <span className="font-bold text-green-600">${betAmount}</span> on
                  <br/>
                  <span className="font-bold text-lg">
                    {currentMarket.options.find(o => o.id === selectedOption)?.text}
                  </span>
                </p>

                <div className="bg-gray-100 rounded-lg p-4 mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">New Balance:</span>
                    <span className="font-bold">${currentUser.balance - betAmount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Potential Win:</span>
                    <span className="font-bold text-green-600">
                      ${(betAmount * (100 / (currentMarket.options.find(o => o.id === selectedOption)?.probability || 50))).toFixed(0)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setShowBetSuccess(false)}
                    className="w-full py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all hover:scale-105"
                  >
                    Place Another Bet
                  </button>
                  <a
                    href="/philosophy"
                    className="block w-full py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300"
                  >
                    📖 Read: The Philosophy of Tiny Steps
                  </a>
                  <button
                    onClick={() => {
                      setShowBetSuccess(false);
                      setMode('browse');
                      navigate('/markets');
                    }}
                    className="w-full py-3 border-2 border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 transition-all hover:scale-105"
                  >
                    Browse More Markets
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => {
              setMode('home');
              navigate('/');
            }}
            className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2 transition-all hover:translate-x-[-4px]"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>

          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h1 className="text-3xl font-bold mb-4">{currentMarket.question}</h1>
            {currentMarket.description && (
              <p className="text-gray-600 mb-6">{currentMarket.description}</p>
            )}

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-sm text-gray-500 mb-1">Total Pool</div>
                <div className="text-2xl font-bold text-green-600">
                  ${(currentMarket.totalPool || 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-sm text-gray-500 mb-1">Ends</div>
                <div className="text-2xl font-bold">
                  {new Date(currentMarket.endDate).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Market Visualization */}
            <div className="mb-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
              <h3 className="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
                📊 Live Market Distribution
                <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full animate-pulse">
                  LIVE
                </span>
              </h3>
              <div className="space-y-4">
                {currentMarket.options.map(option => {
                  // Calculate based on local bets if pool is empty
                  const optionBets = userBets.filter(b =>
                    b.marketId === currentMarket.id && b.optionId === option.id
                  );
                  const betAmount = optionBets.reduce((sum, bet) => sum + bet.amount, 0) || option.totalBets || 0;

                  const allMarketBets = userBets.filter(b => b.marketId === currentMarket.id);
                  const actualPool = allMarketBets.reduce((sum, bet) => sum + bet.amount, 0) || currentMarket.totalPool || 0;

                  const percentage = actualPool > 0 ? (betAmount / actualPool * 100).toFixed(1) : '0.0';

                  return (
                    <div key={option.id} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">{option.text}</span>
                        <div className="text-right">
                          <span className="text-gray-600 text-sm">
                            ${betAmount.toLocaleString()}
                          </span>
                          <span className="ml-2 font-bold text-sm" style={{ color: option.color }}>
                            ({percentage}%)
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden relative">
                        <div
                          className="h-full flex items-center px-2 text-xs text-white font-bold transition-all duration-500"
                          style={{
                            width: `${Math.max(Number(percentage), 5)}%`,
                            backgroundColor: option.color || '#10b981'
                          }}
                        >
                          {percentage}%
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Market Stats */}
              <div className="mt-6 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">📈 Market Activity</span>
                  <div className="font-bold text-green-600">
                    {(() => {
                      const betCount = userBets.filter(b => b.marketId === currentMarket.id).length;
                      return betCount === 1 ? '1 bet placed' : `${betCount} bets placed`;
                    })()}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-gray-500">⏰ Time Remaining</span>
                  <div className="font-bold text-orange-600">
                    {(() => {
                      const end = new Date(currentMarket.endDate);
                      const now = new Date();
                      const hours = Math.floor((end.getTime() - now.getTime()) / (1000 * 60 * 60));
                      return hours > 24 ? `${Math.floor(hours / 24)} days` : `${hours} hours`;
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-xl font-bold mb-2">Market Options</h2>

            {/* Check if user has already placed a bet on this market */}
            {(() => {
              const userMarketBets = userBets.filter(b => b.marketId === currentMarket.id);
              const hasPlacedBet = userMarketBets.length > 0;
              const userBetOption = hasPlacedBet ? userMarketBets[0].optionId : null;
              const totalBetAmount = userMarketBets.reduce((sum, bet) => sum + bet.amount, 0);

              if (hasPlacedBet) {
                return (
                  <p className="text-sm text-green-600 mb-4 font-medium">
                    ✅ You've placed {totalBetAmount} points on this market
                  </p>
                );
              }
              return (
                <p className="text-sm text-gray-600 mb-4">Select your prediction by clicking an option below:</p>
              );
            })()}

            <div className="space-y-3 mb-8">
              {currentMarket.options && currentMarket.options.map(option => {
                const userMarketBets = userBets.filter(b => b.marketId === currentMarket.id);
                const hasPlacedBet = userMarketBets.length > 0;
                const userBetOnThisOption = userMarketBets.some(b => b.optionId === option.id);
                const userBetAmount = userMarketBets
                  .filter(b => b.optionId === option.id)
                  .reduce((sum, bet) => sum + bet.amount, 0);

                return (
                <button
                  key={option.id}
                  onClick={() => !hasPlacedBet && setSelectedOption(option.id)}
                  disabled={hasPlacedBet}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    userBetOnThisOption
                      ? 'border-green-500 bg-green-50 shadow-lg cursor-not-allowed'
                      : hasPlacedBet
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : selectedOption === option.id
                      ? 'border-green-500 bg-green-500 bg-opacity-10 shadow-lg transform hover:scale-[1.02] cursor-pointer'
                      : 'border-gray-300 hover:border-green-400 hover:shadow-md bg-white transform hover:scale-[1.02] cursor-pointer'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        {userBetOnThisOption && (
                          <span className="text-green-500 font-bold">✅</span>
                        )}
                        {!hasPlacedBet && selectedOption === option.id && (
                          <span className="text-green-500">✓</span>
                        )}
                        <div className="font-bold text-lg">{option.text}</div>
                      </div>
                      <div className="text-sm text-gray-500">
                        ${(option.totalBets || 0).toLocaleString()} wagered
                        {userBetOnThisOption && (
                          <span className="text-green-600 font-medium"> (You: ${userBetAmount})</span>
                        )}
                      </div>

                      {/* Betting distribution bar */}
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            option.probability > 50 ? 'bg-green-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${option.probability}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold" style={{ color: option.color || '#10b981' }}>
                        {option.probability}%
                      </div>
                      <div className="text-sm text-gray-500">probability</div>
                    </div>
                  </div>
                </button>
                );
              })}
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4">Place Your Bet</h3>
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-sm text-gray-600 mb-2">Amount</label>
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                    min="1"
                    max={currentUser.balance}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-gray-600 mb-2">Your Balance</label>
                  <div className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-bold">
                    ${currentUser.balance}
                  </div>
                </div>
              </div>
              {(() => {
                const hasAlreadyBet = userBets.some(b => b.marketId === currentMarket.id);
                return (
                  <button
                    onClick={() => handlePlaceBet(currentMarket, selectedOption)}
                    disabled={hasAlreadyBet || !selectedOption || betAmount <= 0 || betAmount > currentUser.balance || isPlacingBet}
                    className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all"
                  >
                    {hasAlreadyBet ? '✅ Already Bet on This Market' :
                     isPlacingBet ? 'Placing Bet...' :
                     currentUser.id.startsWith('demo-') ? '🔐 Login to Place Bet' :
                     'Place Bet'}
                  </button>
                );
              })()}

              {/* Engineer Mode Toggle */}
              <div className="mt-6 p-4 bg-black rounded-lg">
                <button
                  onClick={() => setEngineerMode(!engineerMode)}
                  className="w-full py-2 bg-yellow-400 text-black font-bold rounded hover:bg-yellow-300 transition-all"
                >
                  🔧 {engineerMode ? 'EXIT' : 'ENTER'} ENGINEER MODE
                </button>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  See exactly what happens when you click Place Bet
                </p>
              </div>

              {/* Engineer View Panel */}
              {engineerMode && (
                <div className="mt-4 space-y-4">
                  <div className="p-4 bg-black text-green-400 font-mono text-xs rounded-lg">
                    <div className="mb-2 text-yellow-400">⚡ ENGINEER MODE ACTIVE</div>
                    <div className="space-y-1">
                      <div>→ Current User: {currentUser.email || currentUser.name}</div>
                      <div>→ Balance: ${currentUser.balance}</div>
                      <div>→ Selected Option: {selectedOption || 'none'}</div>
                      <div>→ Bet Amount: ${betAmount}</div>
                      <div>→ Market ID: {currentMarket.id}</div>
                      <div>→ Market Slug: {currentMarket.slug || 'none'}</div>
                      <div className="pt-2 text-yellow-400">WHEN YOU CLICK PLACE BET:</div>
                      <div>1. Check auth (session in localStorage)</div>
                      <div>2. Validate bet amount vs balance</div>
                      <div>3. POST to /api/bets with bet data</div>
                      <div>4. Update local state on success</div>
                      <div>5. Show success/error message</div>
                    </div>
                  </div>

                  {/* Event Log */}
                  <div className="p-4 bg-black text-green-400 font-mono text-xs rounded-lg max-h-96 overflow-y-auto">
                    <div className="mb-2 text-yellow-400">📡 EVENT LOG (Last {engineerEvents.length} events)</div>
                    {engineerEvents.length === 0 ? (
                      <div className="text-gray-500">No events yet. Navigate or interact to see events.</div>
                    ) : (
                      <div className="space-y-1">
                        {engineerEvents.map((event, i) => (
                          <div key={i} className={`border-l-2 pl-2 ${
                            event.type === 'error' ? 'border-red-500 text-red-400' :
                            event.type === 'success' ? 'border-green-500 text-green-400' :
                            event.type === 'api' ? 'border-blue-500 text-blue-400' :
                            'border-gray-500 text-gray-400'
                          }`}>
                            <span className="text-gray-500">[{event.time}]</span>
                            <span className="font-bold"> {event.type.toUpperCase()}: </span>
                            {event.message}
                            {event.data && (
                              <div className="ml-4 text-xs text-gray-500 truncate">
                                {JSON.stringify(event.data).slice(0, 100)}...
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setEngineerEvents([])}
                      className="mt-2 text-xs text-gray-500 hover:text-gray-400"
                    >
                      Clear Log
                    </button>
                  </div>
                </div>
              )}

              {/* Original link */}
              <div className="mt-3 text-center">
                <a
                  href="/bet-engineer"
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  🔧 View Engineer Mode (See what happens when you click)
                </a>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-gray-500">
              Market ID: {currentMarket.id} | Created: {currentMarket.createdAt ? new Date(currentMarket.createdAt).toLocaleDateString() : 'Recently'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default return
  return null;
}